import { isBoolean } from "lodash";
import { useMemo } from "react";
import { useTenantSettings } from "hooks/use-tenant-settings";
export var Permissions;
(function (Permissions) {
    Permissions[Permissions["ADMIN"] = 0] = "ADMIN";
    Permissions[Permissions["ANALYTICS"] = 1] = "ANALYTICS";
    Permissions[Permissions["MARKETS"] = 2] = "MARKETS";
    Permissions[Permissions["CFF"] = 3] = "CFF";
    Permissions[Permissions["COMMITMENTPACING"] = 4] = "COMMITMENTPACING";
    Permissions[Permissions["PERMISSIONS"] = 5] = "PERMISSIONS";
    Permissions[Permissions["CONFIGURE_PERMISSIONS"] = 6] = "CONFIGURE_PERMISSIONS";
    Permissions[Permissions["CREATE_PORTFOLIO"] = 7] = "CREATE_PORTFOLIO";
    Permissions[Permissions["UPDATE_DELETE_PORTFOLIO"] = 8] = "UPDATE_DELETE_PORTFOLIO";
    Permissions[Permissions["IMPORT_DATA"] = 9] = "IMPORT_DATA";
    Permissions[Permissions["EXPORT_DATA"] = 10] = "EXPORT_DATA";
    Permissions[Permissions["UPLOAD_CUSTOM_BENCHMARK"] = 11] = "UPLOAD_CUSTOM_BENCHMARK";
    Permissions[Permissions["CLICKTHROUGH"] = 12] = "CLICKTHROUGH";
    Permissions[Permissions["ACCESS_SHARED_FOLDER"] = 13] = "ACCESS_SHARED_FOLDER";
    Permissions[Permissions["DATA"] = 14] = "DATA";
    Permissions[Permissions["CAMPAIGN"] = 15] = "CAMPAIGN";
    Permissions[Permissions["DOCUMENTSERVICE"] = 16] = "DOCUMENTSERVICE";
    Permissions[Permissions["MARKETMAP"] = 17] = "MARKETMAP";
    Permissions[Permissions["IDD"] = 18] = "IDD";
    Permissions[Permissions["JCURVES"] = 19] = "JCURVES";
    Permissions[Permissions["PEER_COMPARISON"] = 20] = "PEER_COMPARISON";
    Permissions[Permissions["CUSTOM_INDICES"] = 21] = "CUSTOM_INDICES";
    Permissions[Permissions["ACCESS_DATAPOINTS"] = 22] = "ACCESS_DATAPOINTS";
    Permissions[Permissions["ACCESS_USER_SETTINGS"] = 23] = "ACCESS_USER_SETTINGS";
    Permissions[Permissions["ACCESS_MARKETS"] = 24] = "ACCESS_MARKETS";
    Permissions[Permissions["ACCESS_STANDARD_ACTIONS"] = 25] = "ACCESS_STANDARD_ACTIONS";
    Permissions[Permissions["ACCESS_CAMPAIGN"] = 26] = "ACCESS_CAMPAIGN";
    Permissions[Permissions["ACCESS_COMMITMENTPACING"] = 27] = "ACCESS_COMMITMENTPACING";
    Permissions[Permissions["ACCESS_PREQIN_PRO"] = 28] = "ACCESS_PREQIN_PRO";
    Permissions[Permissions["LIGHT_PORTFOLIO_ANALYTICS"] = 29] = "LIGHT_PORTFOLIO_ANALYTICS";
    Permissions[Permissions["FILE_PREVIEW"] = 30] = "FILE_PREVIEW";
    Permissions[Permissions["ACCESS_DOCUMENTLIBRARY"] = 31] = "ACCESS_DOCUMENTLIBRARY";
})(Permissions || (Permissions = {}));
const permissionValidator = (permissions) => (flag) => () => {
    if (permissions && flag in permissions && isBoolean(permissions[flag])) {
        return Boolean(permissions[flag]);
    }
    if (permissions.userActions && flag in permissions.userActions && isBoolean(permissions.userActions[flag])) {
        return Boolean(permissions.userActions[flag]);
    }
    return false;
};
export const usePermission = (perms) => {
    const [tenantSettings] = useTenantSettings();
    const permissionMap = useMemo(() => {
        const validator = permissionValidator(tenantSettings);
        return {
            [Permissions.ADMIN]: validator("admin"),
            [Permissions.ANALYTICS]: validator("insightAnalytics"),
            [Permissions.MARKETS]: validator("insightResearch"),
            [Permissions.CFF]: validator("cff"),
            [Permissions.COMMITMENTPACING]: validator("commitmentPacing"),
            [Permissions.PERMISSIONS]: validator("permissions"),
            [Permissions.CONFIGURE_PERMISSIONS]: validator("configurePermissions"),
            [Permissions.CREATE_PORTFOLIO]: validator("createPortfolio"),
            [Permissions.UPDATE_DELETE_PORTFOLIO]: validator("updatePortfolio"),
            [Permissions.IMPORT_DATA]: validator("importData"),
            [Permissions.EXPORT_DATA]: validator("exportData"),
            [Permissions.UPLOAD_CUSTOM_BENCHMARK]: validator("uploadBenchmarks"),
            [Permissions.CLICKTHROUGH]: validator("clickthrough"),
            [Permissions.ACCESS_SHARED_FOLDER]: validator("accessSharedFolder"),
            [Permissions.DATA]: validator("dataImportExportAdministration"),
            [Permissions.CAMPAIGN]: validator("campaignDashboard"),
            [Permissions.DOCUMENTSERVICE]: validator("documentService"),
            [Permissions.MARKETMAP]: validator("marketMap"),
            [Permissions.IDD]: validator("idd"),
            [Permissions.JCURVES]: validator("jCurves"),
            [Permissions.PEER_COMPARISON]: validator("peerInvestorComparison"),
            [Permissions.CUSTOM_INDICES]: validator("customIndices"),
            [Permissions.ACCESS_DATAPOINTS]: validator("accessDatapoints"),
            [Permissions.ACCESS_MARKETS]: validator("accessMarkets"),
            [Permissions.ACCESS_USER_SETTINGS]: validator("accessUserSettings"),
            [Permissions.ACCESS_STANDARD_ACTIONS]: validator("accessStandardActions"),
            [Permissions.ACCESS_CAMPAIGN]: validator("accessCampaign"),
            [Permissions.ACCESS_COMMITMENTPACING]: validator("accessCommitmentPacing"),
            [Permissions.ACCESS_PREQIN_PRO]: validator("accessPreqinPro"),
            [Permissions.LIGHT_PORTFOLIO_ANALYTICS]: validator("lightPortfolioAnalytics"),
            [Permissions.ACCESS_DOCUMENTLIBRARY]: validator("accessDocumentLibrary"),
            [Permissions.FILE_PREVIEW]: () => validator("clickthrough")() || validator("documentService")()
        };
    }, [tenantSettings]);
    return perms.every((permission) => permissionMap[permission]());
};
